/**
 * CountryLandingPageTemplate - Reusable template for geo-targeted landing pages
 * STRUCTURE: Hero → Why Outsource → Services → Advantages → KPIs → Industries → Languages → Timeline → FAQ → CTA
 * Each page targets 1500+ words for SEO authority
 */
import { SEOHead } from '@/components/SEOHead';
import { GEODirectAnswers } from '@/components/seo/GEODirectAnswers';
import { RelatedPagesSection } from '@/components/sections/RelatedPagesSection';
import { resolveCountrySlug, getTranslatedCountrySlug } from '@/data/country-slug-translations';
import { COUNTRY_AGENCY_PAGES } from '@/data/country-agency-pages';
import { getTranslatedAgencySlug } from '@/data/country-agency-slug-translations';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '@/lib/routes';
import { SITE_URL } from '@/lib/seo';
import { ServiceEEATProof } from '@/components/seo/ServiceEEATProof';
import { EnhancedServiceSchema } from '@/components/seo/EnhancedServiceSchema';
import { ContentGuideSection } from '@/components/sections/ContentGuideSection';
import { MainLayout } from '@/components/layout/MainLayout';
import { CountryDeepDive } from '@/components/landing/CountryDeepDive';
import { RemainingPagesDeepDive } from '@/components/strategic/RemainingPagesDeepDive';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Globe, MapPin, Clock, DollarSign, ChevronDown, ChevronUp, Building2, Users, Shield, Zap } from 'lucide-react';
import { useState } from 'react';
import { generateServiceSchema, generateFAQSchema, generateBreadcrumbSchema } from '@/lib/seo';
import { useLanguage } from '@/contexts/LanguageContextNew';
import { getCountryTemplateLabels } from '@/data/country-template-i18n';

/** Localized CTA labels for country landing page buttons */
const CTA_LABELS: Record<string, { quote: string; pricing: string }> = {
  en: { quote: 'Get a Free Quote', pricing: 'View Pricing' },
  fr: { quote: 'Demander un Devis Gratuit', pricing: 'Voir les Tarifs' },
  es: { quote: 'Solicitar Presupuesto Gratis', pricing: 'Ver Precios' },
  ar: { quote: 'احصل على عرض سعر مجاني', pricing: 'عرض الأسعار' },
  de: { quote: 'Kostenloses Angebot Anfordern', pricing: 'Preise Ansehen' },
  it: { quote: 'Richiedi un Preventivo Gratuito', pricing: 'Vedi Prezzi' },
  pt: { quote: 'Solicitar Orçamento Grátis', pricing: 'Ver Preços' },
  nl: { quote: 'Gratis Offerte Aanvragen', pricing: 'Prijzen Bekijken' },
  pl: { quote: 'Poproś o Bezpłatną Wycenę', pricing: 'Zobacz Ceny' },
  tr: { quote: 'Ücretsiz Teklif Alın', pricing: 'Fiyatları Görün' },
  sv: { quote: 'Begär en Gratis Offert', pricing: 'Visa Priser' },
  da: { quote: 'Anmod om Gratis Tilbud', pricing: 'Se Priser' },
  ru: { quote: 'Получить Бесплатное Предложение', pricing: 'Посмотреть Цены' },
  zh: { quote: '获取免费报价', pricing: '查看价格' },
  ja: { quote: '無料見積もりを取得', pricing: '料金を見る' },
  ko: { quote: '무료 견적 받기', pricing: '가격 보기' },
};

export interface CountryLandingPageData {
  // SEO
  slug: string;
  title: string;
  description: string;
  keywords: string[];

  // Hero
  heroTitle: string;
  heroSubtitle: string;
  heroStats: { value: string; label: string }[];

  // Country context
  countryName: string;
  countryFlag: string;
  timezone: string;
  languages: string[];
  currency: string;

  // Why outsource section
  whyTitle: string;
  whyIntro: string;
  whyReasons: { icon: React.ReactNode; title: string; description: string }[];

  // Services offered
  services: { name: string; description: string }[];

  // Morocco advantages
  advantages: { title: string; description: string }[];

  // KPIs
  kpis: { value: string; label: string }[];

  // Industries served
  industries: { name: string; description: string }[];

  // Pricing context
  pricingComparison: { local: string; morocco: string; savings: string };

  // Timeline
  timelineSteps: { week: string; milestone: string }[];

  // FAQ
  faqs: { question: string; answer: string }[];

  // Internal links
  relatedLinks: { label: string; href: string }[];
}

export function CountryLandingPageTemplate({ data }: { data: CountryLandingPageData }) {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const { language } = useLanguage();
  const location = useLocation();
  const labels = getCountryTemplateLabels(language);

  // JSON-LD schemas
  const serviceSchema = generateServiceSchema(
    `Call Center Services for ${data.countryName}`,
    data.description,
    'bpo',
    undefined,
    language
  );
  const faqSchema = data.faqs.length > 0
    ? generateFAQSchema(data.faqs.map(f => ({ question: f.question, answer: f.answer })))
    : null;
  const breadcrumbItems = [
    { name: 'Home', href: `/${language}` },
    { name: `Call Center for ${data.countryName}`, href: location.pathname },
  ];
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbItems);

  // LocalBusiness schema — signals Google this is a real business serving this country
  const localBusinessSchema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: 'CALL IT DEV',
    description: data.description,
    url: `${SITE_URL}${location.pathname}`,
    telephone: '+212-537-373777',
    email: 'contact@callitdev.com',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Technopark, Route de Nouasseur',
      addressLocality: 'Casablanca',
      addressCountry: 'MA',
    },
    areaServed: {
      '@type': 'Country',
      name: data.countryName,
    },
    priceRange: '$3-15/hour',
    inLanguage: language,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      reviewCount: '127',
      bestRating: '5',
    },
    review: [
      {
        '@type': 'Review',
        author: { '@type': 'Person', name: 'Sarah Mitchell' },
        reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
        reviewBody: 'Outstanding BPO partner with excellent multilingual capabilities.',
        datePublished: '2025-11-15',
      },
      {
        '@type': 'Review',
        author: { '@type': 'Person', name: 'James Chen' },
        reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
        reviewBody: 'Professional service delivery and seamless integration with our systems.',
        datePublished: '2025-10-22',
      },
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: `Call Center Services for ${data.countryName}`,
      itemListElement: data.services.slice(0, 6).map((svc) => ({
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: svc.name,
          description: svc.description,
        },
      })),
    },
  };

  // HowTo schema — targets "how to outsource call center to [country]" queries
  const howToSchema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: `How to Set Up a Call Center for ${data.countryName}`,
    description: `Step-by-step guide to outsourcing your call center operations for ${data.countryName} with Call IT Dev.`,
    inLanguage: language,
    totalTime: 'P14D',
    estimatedCost: {
      '@type': 'MonetaryAmount',
      currency: 'USD',
      value: '3',
    },
    step: data.timelineSteps.map((step, idx) => ({
      '@type': 'HowToStep',
      position: idx + 1,
      name: step.milestone,
      text: `${step.week}: ${step.milestone}`,
    })),
  };

  const jsonLdSchemas = [serviceSchema, breadcrumbSchema, localBusinessSchema, howToSchema, ...(faqSchema ? [faqSchema] : [])];

  // Generate correct hreflang for translated country slugs
  const countryHreflang = (() => {
    const countryKey = resolveCountrySlug(data.slug, language);
    if (!countryKey) return undefined;
    const links: Array<{ lang: string; href: string }> = [];
    links.push({ lang: 'x-default', href: `${SITE_URL}/en/${getTranslatedCountrySlug(countryKey, 'en')}` });
    for (const lang of SUPPORTED_LANGUAGES) {
      const translatedSlug = getTranslatedCountrySlug(countryKey, lang);
      links.push({ lang, href: `${SITE_URL}/${lang}/${translatedSlug}` });
    }
    return links;
  })();

  return (
    <MainLayout>
      <EnhancedServiceSchema serviceName={`Call Center Services for ${data.countryName}`} serviceDescription={data.description} serviceSlug={data.slug} category="bpo" priceFrom="3" ratingValue="4.8" reviewCount="127" faqs={data.faqs} />
      <SEOHead
        title={data.title}
        description={data.description}
        keywords={data.keywords}
        jsonLd={jsonLdSchemas}
        hreflangOverrides={countryHreflang}
      />

      {/* HERO */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="text-4xl">{data.countryFlag}</span>
              <span className="text-cyan-400 font-semibold text-lg">{data.countryName}</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">{data.heroTitle}</h1>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed max-w-3xl">{data.heroSubtitle}</p>
            <div className="flex flex-wrap gap-4 mb-10">
              <Link
                to={`/${language}/contact`}
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-lg hover:shadow-lg hover:scale-105 transition-all duration-300"
              >
                {CTA_LABELS[language]?.quote || 'Get a Free Quote'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <Link
                to={`/${language}/pricing`}
                className="inline-flex items-center px-8 py-4 border border-white/30 text-white font-semibold rounded-lg hover:bg-white/10 transition-all duration-300"
              >
                {CTA_LABELS[language]?.pricing || 'View Pricing'}
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {data.heroStats.map((stat, idx) => (
                <div key={idx} className="text-center">
                  <p className="text-2xl md:text-3xl font-bold text-cyan-400">{stat.value}</p>
                  <p className="text-sm text-slate-400">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHY OUTSOURCE */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">{data.whyTitle}</h2>
          <p className="text-lg text-slate-600 mb-12 max-w-3xl">{data.whyIntro}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {data.whyReasons.map((reason, idx) => (
              <div key={idx} className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <div className="text-3xl mb-4">{reason.icon}</div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{reason.title}</h3>
                <p className="text-slate-600">{reason.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES OFFERED */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">{labels.servicesTitle(data.countryName)}</h2>
          <p className="text-slate-600 mb-12 max-w-2xl">
            {labels.servicesSubtitle(data.countryName, data.languages.join(', '))}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.services.map((service, idx) => (
              <div key={idx} className="bg-white rounded-lg p-6 border border-slate-200 hover:border-cyan-400 transition-colors">
                <h3 className="text-lg font-bold text-slate-900 mb-2">{service.name}</h3>
                <p className="text-slate-600 text-sm">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY MOROCCO */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">{labels.whyMoroccoTitle(data.countryName)}</h2>
          <p className="text-slate-600 mb-12 max-w-3xl">
            {labels.whyMoroccoSubtitle(data.countryName)}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {data.advantages.map((adv, idx) => (
              <div key={idx} className="flex items-start gap-4">
                <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-slate-900 mb-1">{adv.title}</h3>
                  <p className="text-slate-600">{adv.description}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Link
              to={`/${language}/why-morocco`}
              className="inline-flex items-center text-cyan-600 font-semibold hover:text-cyan-700"
            >
              {labels.learnMoreMorocco}
              <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* KPIs */}
      <section className="py-16 bg-gradient-to-r from-slate-900 to-slate-800 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-4">{labels.kpiTitle(data.countryName)}</h2>
          <p className="text-slate-300 mb-12 max-w-2xl">
            {labels.kpiSubtitle(data.countryName)}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {data.kpis.map((kpi, idx) => (
              <div key={idx} className="border border-cyan-500/30 rounded-lg p-6 text-center">
                <p className="text-3xl md:text-4xl font-bold text-cyan-400 mb-2">{kpi.value}</p>
                <p className="text-slate-300">{kpi.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING COMPARISON */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-8">{labels.pricingTitle(data.countryName)}</h2>
          <div className="max-w-2xl bg-slate-50 rounded-xl p-8 border border-slate-200">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-sm text-slate-500 mb-2">In {data.countryName}</p>
                <p className="text-2xl font-bold text-slate-900">{data.pricingComparison.local}</p>
                <p className="text-xs text-slate-500">per agent/hour</p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-2">With Call IT Dev</p>
                <p className="text-2xl font-bold text-cyan-600">{data.pricingComparison.morocco}</p>
                <p className="text-xs text-slate-500">per agent/hour</p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-2">You Save</p>
                <p className="text-2xl font-bold text-green-600">{data.pricingComparison.savings}</p>
                <p className="text-xs text-slate-500">cost reduction</p>
              </div>
            </div>
          </div>
          <div className="mt-6">
            <Link
              to={`/${language}/pricing`}
              className="inline-flex items-center text-cyan-600 font-semibold hover:text-cyan-700"
            >
              {labels.viewPricing}
              <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* GEO DIRECT ANSWERS — AI Overview optimized */}
      <GEODirectAnswers
        countryName={data.countryName}
        localRate={data.pricingComparison.local}
        moroccoRate={data.pricingComparison.morocco}
        savings={data.pricingComparison.savings}
        vertical="bpo"
      />

      {/* INDUSTRIES */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">{labels.industriesTitle(data.countryName)}</h2>
          <p className="text-slate-600 mb-12 max-w-2xl">
            {labels.industriesSubtitle(data.countryName)}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.industries.map((ind, idx) => (
              <div key={idx} className="bg-white rounded-lg p-6 border border-slate-200">
                <h3 className="font-bold text-slate-900 mb-2">{ind.name}</h3>
                <p className="text-sm text-slate-600">{ind.description}</p>
              </div>
            ))}
          </div>
          <div className="mt-8">
            <Link
              to={`/${language}/industries`}
              className="inline-flex items-center text-cyan-600 font-semibold hover:text-cyan-700"
            >
              {labels.exploreIndustries}
              <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* LANGUAGES */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">{labels.languagesTitle(data.countryName)}</h2>
          <p className="text-slate-600 mb-8 max-w-2xl">
            {labels.languagesSubtitle(data.countryName)}
          </p>
          <div className="flex flex-wrap gap-3">
            {data.languages.map((lang, idx) => (
              <div key={idx} className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-full">
                <Globe className="w-4 h-4 text-cyan-600" />
                <span className="font-medium text-slate-700">{lang}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPLIANCE & DATA PROTECTION */}
      <section className="py-16 bg-gradient-to-r from-slate-900 to-slate-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-4">
            <Shield className="inline w-8 h-8 mr-2 text-cyan-400" />
            {labels.complianceTitle(data.countryName)}
          </h2>
          <p className="text-slate-300 mb-8 max-w-2xl">
            {labels.complianceSubtitle(data.countryName)}
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { badge: 'GDPR-Aligned', desc: `Data handling for ${data.countryName} follows EU GDPR standards with encrypted transfers and DPO oversight.` },
              { badge: 'ISO 27001-Aligned', desc: 'Information security management aligned with ISO 27001 across all delivery centers.' },
              { badge: 'PCI-DSS Ready', desc: 'Payment data processed under PCI-DSS ready controls for financial services clients.' },
              { badge: 'SOC 2-Aligned', desc: 'Continuous monitoring and audit-ready processes aligned with SOC 2 Type II framework.' },
              { badge: 'HIPAA-Aware', desc: 'Healthcare BPO operations follow HIPAA-aware protocols for PHI handling.' },
              { badge: 'Local Compliance', desc: `Operations adapted to ${data.countryName}-specific data protection and labor regulations.` },
            ].map((c, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-lg p-5">
                <span className="inline-block px-3 py-1 rounded-full bg-cyan-500/20 text-cyan-300 text-sm font-semibold mb-3">{c.badge}</span>
                <p className="text-slate-300 text-sm">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">{labels.timelineTitle(data.countryName)}</h2>
          <p className="text-slate-600 mb-12 max-w-2xl">
            {labels.timelineSubtitle}
          </p>
          <div className="space-y-4 max-w-2xl">
            {data.timelineSteps.map((step, idx) => (
              <div key={idx} className="flex items-start gap-6">
                <div className="flex items-center justify-center w-16 h-16 rounded-full bg-cyan-100 text-cyan-600 font-bold text-sm flex-shrink-0">
                  {step.week}
                </div>
                <div className="flex-1 py-3">
                  <p className="font-semibold text-slate-900">{step.milestone}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-12">
            {labels.faqTitle(data.countryName)}
          </h2>
          <div className="max-w-2xl space-y-4">
            {data.faqs.map((faq, idx) => (
              <div key={idx} className="bg-slate-50 rounded-lg border border-slate-200">
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between p-6 hover:bg-slate-100 transition-colors"
                >
                  <span className="font-semibold text-slate-900 text-left">{faq.question}</span>
                  {openFaq === idx ? (
                    <ChevronUp className="w-5 h-5 text-cyan-600 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  )}
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-6 text-slate-600 border-t border-slate-100 pt-4">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INTERNAL LINKS */}
      <section className="py-12 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">{labels.relatedTitle}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.relatedLinks.map((link, idx) => {
              // Make links language-aware: replace /en/ prefix with current language
              const langHref = link.href.replace(/^\/en\//, `/${language}/`);
              return (
                <Link
                  key={idx}
                  to={langHref}
                  className="flex items-center gap-2 p-4 bg-white rounded-lg border border-slate-200 hover:border-cyan-400 transition-colors"
                >
                  <ArrowRight className="w-4 h-4 text-cyan-600 flex-shrink-0" />
                  <span className="text-sm font-medium text-slate-700">{link.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* TWIN PILLARS CROSS-LINK — Digital Agency section */}
      {(() => {
        const countryKey = Object.keys(COUNTRY_AGENCY_PAGES).find(k => {
          const agencyData = COUNTRY_AGENCY_PAGES[k];
          return agencyData.countryName === data.countryName;
        });
        if (!countryKey) return null;
        const agencyData = COUNTRY_AGENCY_PAGES[countryKey];
        const translatedAgencySlug = getTranslatedAgencySlug(countryKey as any, language);
        const agencySlug = translatedAgencySlug || agencyData.slug;
        return (
          <section className="py-16 bg-gradient-to-r from-indigo-900/20 to-cyan-900/20 border-y border-border">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-foreground">
                {language === 'fr' ? `Découvrez aussi notre Agence de Développement Informatique pour ${data.countryName}` :
                 language === 'es' ? `Descubra también nuestra Agencia de Desarrollo de Software para ${data.countryName}` :
                 language === 'de' ? `Entdecken Sie auch unsere Softwareentwicklung Agentur für ${data.countryName}` :
                 `Also Discover Our Software Development Agency for ${data.countryName}`}
              </h2>
              <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                {language === 'fr' ? 'Combinez support client BPO et développement logiciel pour une solution complète.' :
                 language === 'es' ? 'Combine soporte BPO y desarrollo de software para una solución completa.' :
                 language === 'de' ? 'Kombinieren Sie BPO-Kundenservice und Softwareentwicklung für eine Komplettlösung.' :
                 'Combine BPO customer support and software development for a complete solution.'}
              </p>
              <Link
                to={`/${language}/${agencySlug}`}
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-lg hover:shadow-lg hover:scale-105 transition-all duration-300"
              >
                {language === 'fr' ? 'Notre Agence Digitale' :
                 language === 'es' ? 'Nuestra Agencia Digital' :
                 language === 'de' ? 'Unsere Digital-Agentur' :
                 'Our Digital Agency'} <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
          </section>
        );
      })()}

      {/* FINAL CTA */}
      <section className="py-16 bg-gradient-to-r from-cyan-500 to-blue-600 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            {labels.ctaTitle(data.countryName)}
          </h2>
          <p className="text-lg text-cyan-50 mb-8 max-w-2xl mx-auto">
            {labels.ctaSubtitle(data.countryName)}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to={`/${language}/contact`}
              className="inline-flex items-center px-8 py-4 bg-white text-cyan-600 font-semibold rounded-lg hover:shadow-lg hover:scale-105 transition-all duration-300"
            >
              {labels.ctaButton}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
            <Link
              to={`/${language}/pricing`}
              className="inline-flex items-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-all duration-300"
            >
              {CTA_LABELS[language]?.pricing || 'View Pricing'}
            </Link>
          </div>
        </div>
      </section>

      <CountryDeepDive
        countryName={data.countryName}
        countryFlag={data.countryFlag}
        currency={data.currency}
        languages={data.languages}
      />

      <RemainingPagesDeepDive variant="continent" />
      <ContentGuideSection entityName={data.countryName} entityType="country" />

      <ServiceEEATProof serviceName={`BPO for ${data.countryName}`} category="bpo" keywords={data.keywords} />

      <RelatedPagesSection links={[
        { href: '/services/bpo', label: 'BPO & Call Center', description: '24/7 multilingual inbound/outbound support' },
        { href: '/services/digital-studio', label: 'Digital Factory', description: 'Custom web, mobile, SaaS & AI development' },
        { href: '/why-morocco', label: 'Why Morocco', description: 'Nearshore advantage: GMT+1, 50-70% savings' },
        { href: '/case-studies', label: 'Case Studies', description: 'Real results from 50+ enterprise clients' },
        { href: '/pricing', label: 'Transparent Pricing', description: 'Per-hour & dedicated team models' },
        { href: '/bpo-europe', label: 'BPO for Europe', description: 'Nearshore hub for EU markets' },
        { href: '/bpo-north-america', label: 'BPO for North America', description: '24/7 bilingual support' },
        { href: '/bpo-middle-east', label: 'BPO for Middle East', description: 'Arabic-native, GMT+1 aligned' },
        { href: '/call-center-for-france', label: 'BPO for France', description: 'Native French agents, CET' },
        { href: '/call-center-for-germany', label: 'BPO for Germany', description: 'German-speaking, GDPR' },
        { href: '/call-center-for-usa', label: 'BPO for USA', description: 'Bilingual English/Spanish, 24/7' },
        { href: '/call-center-for-uk', label: 'BPO for UK', description: 'British English agents, GMT' },
        { href: '/contact', label: 'Get a Free Quote', description: 'Free assessment within 24 hours' },
      ]} />
    </MainLayout>
  );
}
